import { useEffect, useCallback } from 'react';
import { ff } from '@atlassian/jira-feature-flagging';
import { getDelta } from '@atlassian/jira-portfolio-3-common/src/custom-scrollbar';
import { useTimelineRuler, useHorizontalScrolling } from '../../../controllers';

function useScrollingContainerVSync(scrollableEl: HTMLElement | null, paddingSize: number) {
	const [{ viewport }, { nudge }] = useHorizontalScrolling();
	const [{ msToPx }] = useTimelineRuler();

	useEffect(() => {
		const offsetInPx = msToPx(viewport.offset) || 0;

		if (scrollableEl) {
			if (ff('portfolio-3-no-param-reassign-fix_topgz')) {
				// using `scrollLeft` before causes the scroll action happen instantly, so using behaviour of `instant` to do the same (ref: https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollLeft, )
				// The scrollLeft property can be set, which causes the element to scroll to the specified horizontal position, in the same way as using Element.scroll() with behavior: "auto".
				scrollableEl.scrollTo({
					left: paddingSize + offsetInPx,
				});
			} else {
				// eslint-disable-next-line no-param-reassign
				scrollableEl.scrollLeft = paddingSize + offsetInPx;
			}
		}
	}, [scrollableEl, viewport, msToPx, paddingSize]);

	const handleWheel = useCallback(
		(e: WheelEvent) => {
			const { deltaX, deltaY } = getDelta(e);

			if (Math.abs(deltaX) >= Math.abs(deltaY)) {
				nudge(deltaX);
				e.preventDefault();
			}
		},
		[nudge],
	);

	useEffect(() => {
		if (!scrollableEl) {
			return;
		}

		scrollableEl.addEventListener('wheel', handleWheel, false);
		return () => scrollableEl.removeEventListener('wheel', handleWheel, false);
	}, [scrollableEl, handleWheel]);
}

type Props = {
	paddingSize: number;
	scrollableRef: {
		current: HTMLElement | null;
	};
};

/** Sync the scrolling state within all scroll views. */
export default function ViewportVSync({
	scrollableRef: { current: scrollableEl },
	paddingSize,
}: Props) {
	useScrollingContainerVSync(scrollableEl, paddingSize);
	return null;
}
